import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import RefreshButton from '../components/RefreshButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  auditLogService,
  AUDIT_ACTION_LABELS,
  AUDIT_RESOURCE_LABELS,
  AUDIT_RESULT_LABELS,
  auditChangeRows,
  auditFailure,
  type AuditLogEntry,
  type AuditResult,
} from '../services/audit-log.service';
import { userService } from '../services/user.service';
import { groupService } from '../services/group.service';
import { useAuthStore } from '@/stores/authStore';
import { useErrorStore } from '@/stores/errorStore';
import type { Group, User } from '@/types/user.types';
import { AUDIT_ACTION_ROLES, AUDIT_RESULTS } from '@/types/enums.generated';

// The date filters are calendar days as the viewer sees them; the backend
// needs the viewer's zone to turn them into instants (QA #59: read as UTC, a
// UTC+8 viewer's「起始日」skipped 00:00–07:59 of that day).
function browserTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

// QA #98: the server resolves the actor; a removed user falls back to the id.
function actorName(row: AuditLogEntry): string {
  return row.username || `${row.user_id.slice(0, 8)}…`;
}

// Rows written before the group column was filled (QA #98), or by a user
// in no group, have no group (decided: no backfill).
function groupLabel(row: AuditLogEntry): string {
  return row.group_name || '—';
}

function ResultChip({ result }: { result?: AuditResult }) {
  const r: AuditResult = result ?? 'success';
  return (
    <Chip
      size="small"
      variant="outlined"
      color={r === 'failure' ? 'error' : 'success'}
      label={AUDIT_RESULT_LABELS[r]}
    />
  );
}

// 失敗原因（QA #99）：狀態碼、錯誤代碼、訊息與各欄位原因。
function FailureReason({ row }: { row: AuditLogEntry }) {
  const f = auditFailure(row.details);
  return (
    <Alert severity="error" variant="outlined" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
      <Typography variant="body2">
        <b>失敗原因：</b>
        {f.message || '（伺服器未提供訊息）'}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div">
        {f.status !== undefined && `HTTP ${f.status}`}
        {f.code && ` · 錯誤代碼 ${f.code}`}
      </Typography>
      {f.fields.length > 0 && (
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
          {f.fields.map(([k, v]) => (
            <li key={k}>
              <Typography variant="caption">
                <code>{k}</code>：{v}
              </Typography>
            </li>
          ))}
        </Box>
      )}
    </Alert>
  );
}

// 稽核紀錄（A-04 / P-5）。後端 GET /api/v1/audit-logs 早已完整（admin / sub_admin，
// RBAC 範圍化），前端一直沒有頁面，資安稽核要看只能查資料庫。
export default function AuditLogs() {
  const showError = useErrorStore((s) => s.showError);

  const [rows, setRows] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });

  // 篩選
  const [action, setAction] = useState('');
  const [userId, setUserId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // QA #78: exact match or prefix — `10.0.` finds the whole range.
  const [ip, setIp] = useState('');
  const [groupId, setGroupId] = useState('');
  const [result, setResult] = useState<AuditResult | ''>('');
  // Only the user filter's options: names in the table come from the server
  // (QA #98 — the page used to resolve them from this list, capped at 100).
  const [users, setUsers] = useState<User[]>([]);
  // The group filter is admin-only: GET /groups is admin-only, and a
  // sub_admin's rows are already limited to their groups.
  const [groups, setGroups] = useState<Group[]>([]);

  const [detail, setDetail] = useState<AuditLogEntry | null>(null);

  // The user filter needs the user list, which only admin / sub_admin may
  // read (GET /users is role-gated). An operator's own audit log (SRS
  // FR-105) must not trip that 403 — QA #36: the page said「Forbidden」to
  // every operator although the log itself was open to them.
  const canManageUsers = useAuthStore((s) => s.canManageUsers);
  const isAdmin = useAuthStore((s) => s.isAdmin);
  // QA #43: offer only the actions the viewer's scope can contain. A
  // sub_admin sees their group's users, none of whom can delete a group or
  // purge a user; an operator sees only themself. AUDIT_ACTION_ROLES is
  // generated from the route guards (backend audit-logs ActionRoles).
  const role = useAuthStore((s) => s.user?.role);
  const actionOptions = useMemo(
    () =>
      Object.entries(AUDIT_ACTION_LABELS).filter(
        ([k]) => !role || ((AUDIT_ACTION_ROLES as Record<string, readonly string[]>)[k] ?? []).includes(role)
      ),
    [role]
  );
  useEffect(() => {
    if (!canManageUsers()) return;
    userService
      .listUsers({ page_size: 100 })
      .then((res) => setUsers(res?.items ?? []))
      .catch(console.error);
  }, [canManageUsers]);
  useEffect(() => {
    if (!isAdmin()) return;
    groupService
      .listGroups()
      .then((res) => setGroups(res ?? []))
      .catch(console.error);
  }, [isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await auditLogService.list({
        action: action || undefined,
        user_id: userId || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        tz: startDate || endDate ? browserTimeZone() : undefined,
        ip: ip.trim() || undefined,
        group_id: groupId || undefined,
        result: result || undefined,
        page: paginationModel.page + 1, // MUI 從 0 開始，API 從 1 開始
        limit: paginationModel.pageSize,
      });
      setRows(res.data);
      setTotal(res.pagination.total);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
      showError('稽核紀錄載入失敗');
    } finally {
      setLoading(false);
    }
  }, [action, userId, startDate, endDate, ip, groupId, result, paginationModel, showError]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo<GridColDef<AuditLogEntry>[]>(
    () => [
      {
        field: 'timestamp',
        headerName: '時間',
        width: 170,
        valueFormatter: (value: string) => (value ? new Date(value).toLocaleString('zh-TW') : '-'),
      },
      {
        field: 'action',
        headerName: '動作',
        width: 110,
        renderCell: (params) => (
          <Chip size="small" label={AUDIT_ACTION_LABELS[params.value] ?? params.value} />
        ),
      },
      {
        field: 'resource_type',
        headerName: '資源',
        width: 100,
        valueFormatter: (value: string) => AUDIT_RESOURCE_LABELS[value] ?? value,
      },
      {
        field: 'resource_id',
        headerName: '資源 ID',
        width: 130,
        renderCell: (params) =>
          params.value ? (
            <Tooltip title={params.value} arrow>
              <span style={{ fontFamily: 'monospace' }}>{String(params.value).slice(0, 8)}…</span>
            </Tooltip>
          ) : (
            '-'
          ),
      },
      {
        field: 'result',
        headerName: '結果',
        width: 80,
        renderCell: (params) => <ResultChip result={params.row.result} />,
      },
      {
        field: 'username',
        headerName: '使用者',
        flex: 0.8,
        minWidth: 120,
        valueGetter: (_value: unknown, row: AuditLogEntry) => actorName(row),
      },
      {
        field: 'group_name',
        headerName: '群組',
        flex: 0.6,
        minWidth: 100,
        valueGetter: (_value: unknown, row: AuditLogEntry) => groupLabel(row),
      },
      { field: 'ip_address', headerName: '來源 IP', width: 140 },
      {
        field: 'details',
        headerName: '詳情',
        width: 70,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <IconButton size="small" onClick={() => setDetail(params.row)} aria-label="查看詳情">
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [],
  );

  // QA #99: a before / after pair is shown as a table; the raw JSON below
  // keeps the rest (method, path, request id, error fields).
  const changes = detail ? auditChangeRows(detail.details) : null;
  const rawDetails = useMemo(() => {
    if (!detail?.details) return {};
    if (!changes) return detail.details;
    return Object.fromEntries(Object.entries(detail.details).filter(([k]) => k !== 'before' && k !== 'after'));
  }, [detail, changes]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            稽核紀錄
          </Typography>
          <Typography variant="body2" color="text.secondary">
            誰在什麼時候對哪個資源做了什麼，包含被拒絕或失敗的變更操作。sub_admin 只看得到自己群組的紀錄。
          </Typography>
        </Box>
        <RefreshButton onClick={load} loading={loading} />
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>動作</InputLabel>
            <Select
              value={action}
              label="動作"
              onChange={(e) => {
                setAction(e.target.value);
                setPaginationModel((m) => ({ ...m, page: 0 }));
              }}
            >
              <MenuItem value="">全部</MenuItem>
              {actionOptions.map(([k, v]) => (
                <MenuItem key={k} value={k}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {canManageUsers() && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>使用者</InputLabel>
              <Select
                value={userId}
                label="使用者"
                onChange={(e) => {
                  setUserId(e.target.value);
                  setPaginationModel((m) => ({ ...m, page: 0 }));
                }}
              >
                <MenuItem value="">全部</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            size="small"
            type="date"
            label="起始日"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPaginationModel((m) => ({ ...m, page: 0 }));
            }}
          />
          <TextField
            size="small"
            type="date"
            label="結束日"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPaginationModel((m) => ({ ...m, page: 0 }));
            }}
          />
          <TextField
            size="small"
            label="來源 IP"
            placeholder="10.0. 可找整段"
            value={ip}
            onChange={(e) => {
              setIp(e.target.value);
              setPaginationModel((m) => ({ ...m, page: 0 }));
            }}
            sx={{ minWidth: 160 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>結果</InputLabel>
            <Select
              value={result}
              label="結果"
              onChange={(e) => {
                setResult(e.target.value as AuditResult | '');
                setPaginationModel((m) => ({ ...m, page: 0 }));
              }}
            >
              <MenuItem value="">全部</MenuItem>
              {AUDIT_RESULTS.map((r) => (
                <MenuItem key={r} value={r}>
                  {AUDIT_RESULT_LABELS[r]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isAdmin() && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>群組</InputLabel>
              <Select
                value={groupId}
                label="群組"
                onChange={(e) => {
                  setGroupId(e.target.value);
                  setPaginationModel((m) => ({ ...m, page: 0 }));
                }}
              >
                <MenuItem value="">全部</MenuItem>
                {groups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          autoHeight
          density="compact"
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={total}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
        />
      </Paper>

      <Dialog open={detail !== null} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>稽核詳情</DialogTitle>
        <DialogContent>
          {detail && (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Typography variant="body2">
                <b>時間：</b>
                {new Date(detail.timestamp).toLocaleString('zh-TW')}
              </Typography>
              <Typography variant="body2">
                <b>動作：</b>
                {AUDIT_ACTION_LABELS[detail.action] ?? detail.action}（
                {AUDIT_RESOURCE_LABELS[detail.resource_type] ?? detail.resource_type}）
              </Typography>
              <Typography variant="body2" component="div">
                <b>結果：</b>
                <ResultChip result={detail.result} />
              </Typography>
              {detail.result === 'failure' && <FailureReason row={detail} />}
              <Typography variant="body2">
                <b>使用者：</b>
                {actorName(detail)}
              </Typography>
              <Typography variant="body2">
                <b>群組：</b>
                {groupLabel(detail)}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                <b>資源 ID：</b>
                {detail.resource_id ?? '-'}
              </Typography>
              <Typography variant="body2">
                <b>來源 IP：</b>
                {detail.ip_address || '-'}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                <b>User-Agent：</b>
                {detail.user_agent || '-'}
              </Typography>
              {changes && (
                <Box>
                  <Typography variant="body2">
                    <b>變更內容</b>
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>項目</TableCell>
                        <TableCell>改前</TableCell>
                        <TableCell>改後</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {changes.map((c) => (
                        <TableRow key={c.label} sx={c.changed ? { bgcolor: 'action.selected' } : undefined}>
                          <TableCell>{c.label}</TableCell>
                          <TableCell sx={{ wordBreak: 'break-all' }}>{c.before}</TableCell>
                          <TableCell sx={{ wordBreak: 'break-all', fontWeight: c.changed ? 600 : undefined }}>
                            {c.after}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
              <Box>
                <Typography variant="body2">
                  <b>細節</b>
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    fontSize: 12,
                    overflowX: 'auto',
                  }}
                >
                  {JSON.stringify(rawDetails, null, 2)}
                </Box>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>關閉</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
